import React, { useState, useRef, useEffect } from "react";

const PullToRefresh = ({ onRefresh, children }) => {
  const [pulling, setPulling] = useState(false);
  const [pullProgress, setPullProgress] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef(null);
  const startYRef = useRef(0);
  const maxPullRef = useRef(100);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e) => {
      if (container.scrollTop === 0) {
        startYRef.current = e.touches[0].clientY;
        setPulling(true);
      }
    };

    const handleTouchMove = (e) => {
      if (!pulling || container.scrollTop !== 0) {
        setPulling(false);
        return;
      }

      const currentY = e.touches[0].clientY;
      const diff = currentY - startYRef.current;

      if (diff > 0) {
        const progress = Math.min(diff / maxPullRef.current, 1);
        setPullProgress(progress);
      } else {
        setPulling(false);
        setPullProgress(0);
      }
    };

    const handleTouchEnd = async () => {
      if (pulling && pullProgress >= 0.5 && !isRefreshing) {
        setIsRefreshing(true);
        await onRefresh();
        setIsRefreshing(false);
        setPullProgress(0);
        setPulling(false);
      } else {
        setPulling(false);
        setPullProgress(0);
      }
    };

    container.addEventListener("touchstart", handleTouchStart);
    container.addEventListener("touchmove", handleTouchMove);
    container.addEventListener("touchend", handleTouchEnd);

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [pulling, pullProgress, isRefreshing, onRefresh]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        height: "100%",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {/* Pull to refresh indicator */}
      {(pulling || isRefreshing) && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            padding: "16px",
            background: "rgba(5, 14, 26, 0.95)",
            borderBottom: "1px solid rgba(59, 130, 246, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            zIndex: 50,
            transform: `translateY(${Math.max(-40, -40 + pullProgress * 60)}px)`,
            opacity: pulling ? 0.9 : 1,
          }}
        >
          <div
            style={{
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              border: "2px solid #5B9EF0",
              borderTopColor: "transparent",
              animation: isRefreshing ? "spin 1s linear infinite" : "none",
              transform: `rotate(${pullProgress * 180}deg)`,
            }}
          />
          <span style={{ color: "#7A9CC4", fontSize: "14px" }}>
            {isRefreshing ? "Refreshing..." : pullProgress >= 0.5 ? "Release to refresh" : "Pull to refresh"}
          </span>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Spacer when pulling */}
      {pulling && (
        <div
          style={{
            height: `${pullProgress * 80}px`,
            transition: isRefreshing ? "none" : "height 0.2s ease",
          }}
        />
      )}

      {children}
    </div>
  );
};

export default PullToRefresh;
